/* ==========================================================================
   digiawesomeness — theme.js
   No dependencies. Custom elements + small utilities.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  const on = (el, evt, fn, opts) => el && el.addEventListener(evt, fn, opts);
  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

  function formatMoney(cents) {
    const format = (window.themeSettings && window.themeSettings.moneyFormat) || '${{amount}}';
    const value = (cents / 100);

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number || 0).toFixed(precision);
      const parts = number.split('.');
      const intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const decPart = parts[1] ? decimal + parts[1] : '';
      return intPart + decPart;
    }

    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = placeholder ? placeholder[1] : 'amount';
    let formatted;

    switch (key) {
      case 'amount_no_decimals': formatted = withDelimiters(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': formatted = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': formatted = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_space_separator': formatted = withDelimiters(value, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': formatted = withDelimiters(value, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': formatted = withDelimiters(value, 2, "'", '.'); break;
      default: formatted = withDelimiters(value, 2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  function trapFocus(container) {
    const focusables = $$(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      container
    ).filter((el) => el.offsetParent !== null);
    if (!focusables.length) return () => {};

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    function handle(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handle);
    first.focus();
    return () => container.removeEventListener('keydown', handle);
  }

  /* ------------------------------------------------------- overlay manager */

  const Overlay = {
    el: null,
    openCount: 0,
    get node() {
      if (!this.el) this.el = $('[data-overlay]');
      return this.el;
    },
    show() {
      this.openCount++;
      const n = this.node;
      if (!n) return;
      n.hidden = false;
      requestAnimationFrame(() => n.classList.add('is-visible'));
      document.body.classList.add('scroll-locked');
    },
    hide() {
      this.openCount = Math.max(0, this.openCount - 1);
      if (this.openCount > 0) return;
      const n = this.node;
      if (!n) return;
      n.classList.remove('is-visible');
      document.body.classList.remove('scroll-locked');
      setTimeout(() => { if (this.openCount === 0) n.hidden = true; }, 300);
    }
  };

  /* ---------------------------------------------------------- panel base */

  class PanelElement extends HTMLElement {
    constructor() {
      super();
      this.releaseFocus = null;
      this.openerEl = null;
    }

    open(opener) {
      this.openerEl = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      Overlay.show();
      this.releaseFocus = trapFocus(this);
      document.dispatchEvent(new CustomEvent('panel:opened', { detail: { panel: this } }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      Overlay.hide();
      if (this.releaseFocus) { this.releaseFocus(); this.releaseFocus = null; }
      if (this.openerEl && document.contains(this.openerEl)) this.openerEl.focus();
    }

    get isOpen() { return this.classList.contains('is-open'); }
  }

  /* -------------------------------------------------------- mobile nav */

  class MobileNav extends PanelElement {
    connectedCallback() {
      this.setAttribute('aria-hidden', 'true');
      $$('[data-mobile-nav-close]', this).forEach((btn) => on(btn, 'click', () => this.close()));
    }
  }
  customElements.define('mobile-nav', MobileNav);

  /* ------------------------------------------------------- search modal */

  class SearchModal extends PanelElement {
    connectedCallback() {
      this.setAttribute('aria-hidden', 'true');
      $$('[data-search-close]', this).forEach((btn) => on(btn, 'click', () => this.close()));
    }
    open(opener) {
      super.open(opener);
      const input = $('input[type="search"]', this);
      if (input) setTimeout(() => input.focus(), 60);
    }
  }
  customElements.define('search-modal', SearchModal);

  /* --------------------------------------------------------- cart drawer */

  class CartDrawer extends PanelElement {
    connectedCallback() {
      this.setAttribute('aria-hidden', 'true');
      this.bind();
      document.addEventListener('cart:updated', (e) => this.refresh(e.detail && e.detail.openDrawer));
    }

    bind() {
      $$('[data-cart-close]', this).forEach((btn) => on(btn, 'click', () => this.close()));

      this.addEventListener('click', (e) => {
        const changeBtn = e.target.closest('[data-line-change]');
        if (changeBtn) {
          e.preventDefault();
          this.changeLine(changeBtn.dataset.lineKey, parseInt(changeBtn.dataset.lineChange, 10));
        }
      });
    }

    async changeLine(key, quantity) {
      this.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(window.routes.cart_change_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        const cart = await res.json();
        updateCartCount(cart.item_count);
        await this.refresh(false);
      } catch (err) {
        console.error('[cart] line change failed', err);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    async refresh(shouldOpen) {
      try {
        const res = await fetch(window.routes.cart_url + '?section_id=cart-drawer');
        const text = await res.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const fresh = parsed.querySelector('cart-drawer');
        if (fresh) {
          this.innerHTML = fresh.innerHTML;
          this.bind();
        }
      } catch (err) {
        console.error('[cart] refresh failed', err);
      }
      if (shouldOpen && !this.isOpen) this.open();
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* ------------------------------------------- shared product-page updates */

  // Both the standard variant picker and the offer cards drive the same
  // price block and buy button, so the DOM writing lives here once.

  function applyPrice(sectionId, price, compareAt) {
    // The sticky bar is updated first: offer cards hide the standalone price
    // block, so bailing early on a missing #price-… would leave the sticky
    // bar showing the previous variant's price.
    $$('[data-sticky-price]').forEach((el) => { el.textContent = formatMoney(price); });

    const wrap = document.getElementById('price-' + sectionId);
    if (!wrap) return;

    const current = $('[data-price-current]', wrap);
    const compare = $('[data-price-compare]', wrap);
    const saleWord = $('[data-price-sale-word]', wrap);
    const onSale = compareAt > price;

    if (current) current.textContent = formatMoney(price);
    if (compare) {
      compare.textContent = formatMoney(compareAt);
      compare.hidden = !onSale;
    }
    if (saleWord) saleWord.hidden = !onSale;
    wrap.classList.toggle('price--on-sale', onSale);
  }

  function applyAvailability(sectionId, available) {
    $$(`[data-add-to-cart][data-section="${sectionId}"]`).forEach((btn) => {
      const label = $('.btn__label', btn) || btn;
      btn.disabled = !available;
      btn.classList.toggle('is-disabled', !available);
      label.textContent = available
        ? (btn.dataset.labelDefault || window.themeStrings.addToCart)
        : window.themeStrings.soldOut;
    });
  }

  /* -------------------------------------------------- global open triggers */

  on(document, 'click', (e) => {
    const trigger = e.target.closest('[data-open-panel]');
    if (trigger) {
      const target = $(trigger.dataset.openPanel);
      if (target && typeof target.open === 'function') {
        e.preventDefault();
        target.open(trigger);
      }
      return;
    }

    if (e.target.matches('[data-overlay]')) {
      $$('mobile-nav, search-modal, cart-drawer').forEach((p) => p.close && p.close());
    }
  });

  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') {
      $$('mobile-nav, search-modal, cart-drawer').forEach((p) => p.close && p.close());
    }
  });

  /* -------------------------------------------------------- add to cart */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.submitBtn = this.querySelector('[data-add-to-cart]');
      this.errorEl = this.querySelector('[data-form-error]');
      if (this.form) on(this.form, 'submit', this.onSubmit.bind(this));
    }

    get checkedUpsells() {
      return $$('[data-upsell]').filter((el) => {
        const box = el.querySelector('input[type="checkbox"]');
        return box && box.checked && el.dataset.variantId;
      });
    }

    async onSubmit(e) {
      const upsells = this.checkedUpsells;
      const isDrawer = window.themeSettings.cartType === 'drawer';

      // Nothing to intercept: no upsell to bundle in and no drawer to open,
      // so let the browser do its normal POST.
      if (!isDrawer && !upsells.length) return;

      e.preventDefault();
      if (this.submitBtn.getAttribute('aria-busy') === 'true') return;

      this.submitBtn.setAttribute('aria-busy', 'true');
      if (this.errorEl) this.errorEl.hidden = true;

      const formData = new FormData(this.form);
      let request;

      if (upsells.length) {
        // The multi-item form of /cart/add takes JSON, not FormData, so the
        // main product and every ticked upsell go in one request.
        const items = [{
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity') || '1', 10) || 1
        }];
        upsells.forEach((el) => items.push({ id: el.dataset.variantId, quantity: 1 }));

        request = {
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        };
      } else {
        formData.append('sections', 'cart-drawer');
        request = { headers: { Accept: 'application/json' }, body: formData };
      }

      try {
        const res = await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: request.headers,
          body: request.body
        });
        const data = await res.json();

        if (data.status) {
          if (this.errorEl) {
            this.errorEl.textContent = data.description || data.message || window.themeStrings.cartError;
            this.errorEl.hidden = false;
          }
          return;
        }

        const cartRes = await fetch(window.routes.cart_url + '.js');
        const cart = await cartRes.json();
        updateCartCount(cart.item_count);

        // Without a drawer there's nothing to open, so send them to the cart
        // the way the un-intercepted form would have.
        if (!isDrawer) {
          window.location.href = window.routes.cart_url;
          return;
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { openDrawer: true } }));
      } catch (err) {
        console.error('[cart] add failed', err);
        if (this.errorEl) {
          this.errorEl.textContent = window.themeStrings.cartError;
          this.errorEl.hidden = false;
        }
      } finally {
        this.submitBtn.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------------------------------------------------- quick add (cards) */

  on(document, 'submit', async (e) => {
    const form = e.target.closest('[data-quick-add-form]');
    if (!form) return;
    if (window.themeSettings.cartType !== 'drawer') return;

    e.preventDefault();
    const btn = form.querySelector('[data-quick-add]');
    if (btn && btn.getAttribute('aria-busy') === 'true') return;
    if (btn) btn.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);

      const cart = await (await fetch(window.routes.cart_url + '.js')).json();
      updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { openDrawer: true } }));
    } catch (err) {
      console.error('[cart] quick add failed', err);
      window.location.href = form.querySelector('a') ? form.querySelector('a').href : window.routes.cart_url;
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  });

  /* ------------------------------------------------------ variant picker */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.updateUrlEnabled = this.dataset.updateUrl !== 'false';

      const dataEl = this.querySelector('[data-variant-json]');
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];

      this.addEventListener('change', this.onChange.bind(this));
    }

    get selectedOptions() {
      return $$('input:checked, select', this).map((el) => el.value);
    }

    onChange() {
      const chosen = this.selectedOptions;
      const variant = this.variants.find((v) =>
        v.options.every((opt, i) => opt === chosen[i])
      );

      this.renderOptionLabels();

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateInput(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateMedia(variant);
      this.updateUrl(variant);
    }

    renderOptionLabels() {
      $$('[data-option-selected]', this).forEach((el) => {
        const idx = parseInt(el.dataset.optionSelected, 10);
        const input = this.querySelector(`input[name="option-${idx}"]:checked, select[name="option-${idx}"]`);
        if (input) el.textContent = input.value;
      });
    }

    updateInput(variant) {
      const input = $(`#variant-id-${this.sectionId}`);
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updatePrice(variant) {
      applyPrice(this.sectionId, variant.price, variant.compare_at_price || 0);
    }

    updateButton(variant) {
      applyAvailability(this.sectionId, variant.available);
    }

    setUnavailable() {
      $$(`[data-add-to-cart][data-section="${this.sectionId}"]`).forEach((btn) => {
        const label = $('.btn__label', btn) || btn;
        btn.disabled = true;
        btn.classList.add('is-disabled');
        label.textContent = window.themeStrings.unavailable;
      });
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = $(`#gallery-${this.sectionId}`);
      if (!gallery) return;
      const target = $(`[data-media-id="${variant.featured_media.id}"]`, gallery);
      if (target && typeof gallery.selectByElement === 'function') gallery.selectByElement(target);
    }

    updateUrl(variant) {
      if (!this.updateUrlEnabled || !this.productUrl) return;
      window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------------------------------------------------------- offer cards */

  // Variant chooser drawn as stacked offer cards. Each card carries its own
  // variant id, so selecting one writes straight to the buy form rather than
  // matching option values back to a variant.

  class OfferCards extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.updateUrlEnabled = this.dataset.updateUrl !== 'false';
      this.cards = $$('.offer-card', this);

      this.addEventListener('change', (e) => {
        const input = e.target.closest('input[type="radio"]');
        if (!input) return;
        const card = input.closest('.offer-card');
        if (card) this.select(card);
      });

      // product-gallery is defined further down this file, so its select()
      // does not exist yet while this element is upgrading. Deferring the
      // first sync by a microtask lets every custom element finish upgrading
      // first, so landing on ?variant=x shows that variant's image.
      queueMicrotask(() => {
        const preselected = this.querySelector('.offer-card.is-selected') || this.cards[0];
        if (preselected) this.select(preselected, { silent: true });
      });
    }

    select(card, opts) {
      const options = opts || {};

      this.cards.forEach((c) => c.classList.toggle('is-selected', c === card));

      const variantId = card.dataset.variantId;
      const price = parseInt(card.dataset.price, 10) || 0;
      const compareAt = parseInt(card.dataset.compare, 10) || 0;
      const available = card.dataset.available === 'true';

      const input = document.getElementById('variant-id-' + this.sectionId);
      if (input) {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      applyPrice(this.sectionId, price, compareAt);
      applyAvailability(this.sectionId, available);

      if (card.dataset.mediaId) {
        const gallery = document.getElementById(this.dataset.gallery);
        if (gallery && typeof gallery.select === 'function') gallery.select(card.dataset.mediaId);
      }

      // Skip the history write on first paint so we don't clobber a
      // ?variant= the shopper arrived with.
      if (!options.silent && this.updateUrlEnabled && this.productUrl) {
        window.history.replaceState({}, '', `${this.productUrl}?variant=${variantId}`);
      }
    }
  }
  customElements.define('offer-cards', OfferCards);

  /* ------------------------------------------------------ countdown timer */

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.clock = $('[data-clock]', this);
      if (!this.clock) return;
      this.deadline = this.resolveDeadline();
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    resolveDeadline() {
      if (this.dataset.mode === 'fixed') {
        // "2026-12-31 23:59" -> ISO-ish, parsed in the visitor's timezone.
        const parsed = Date.parse(String(this.dataset.end || '').trim().replace(' ', 'T'));
        if (!isNaN(parsed)) return parsed;
      }

      // Rolling window: remember the deadline so a refresh doesn't reset it.
      const hours = parseFloat(this.dataset.hours || '12') || 12;
      const key = this.dataset.storageKey || 'countdown';
      let stored = null;
      try { stored = window.localStorage.getItem(key); } catch (err) { /* private mode */ }

      let end = stored ? parseInt(stored, 10) : NaN;
      if (!end || isNaN(end) || end <= Date.now()) {
        end = Date.now() + hours * 3600 * 1000;
        try { window.localStorage.setItem(key, String(end)); } catch (err) { /* private mode */ }
      }
      return end;
    }

    tick() {
      const remaining = this.deadline - Date.now();

      if (remaining <= 0) {
        clearInterval(this.timer);
        this.textContent = this.dataset.expiredText || '';
        this.hidden = !this.dataset.expiredText;
        return;
      }

      const pad = (n) => String(n).padStart(2, '0');
      const hours = Math.floor(remaining / 3600000);
      const minutes = Math.floor(remaining / 60000) % 60;
      const seconds = Math.floor(remaining / 1000) % 60;
      this.clock.textContent = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }

    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ----------------------------------------------------- review carousel */

  class ReviewCarousel extends HTMLElement {
    connectedCallback() {
      this.reviews = $$('[data-review]', this);
      this.dots = $$('[data-review-dot]', this);
      this.index = 0;
      if (this.reviews.length < 2) return;

      this.dots.forEach((dot) => {
        on(dot, 'click', () => {
          this.show(parseInt(dot.dataset.reviewDot, 10));
          clearInterval(this.timer);
        });
      });

      this.timer = setInterval(() => this.show((this.index + 1) % this.reviews.length), 6000);
    }

    show(i) {
      this.index = i;
      this.reviews.forEach((r, n) => r.classList.toggle('is-active', n === i));
      this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    }

    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('review-carousel', ReviewCarousel);

  /* ---------------------------------------------------- product gallery */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-media-id]', this).filter((el) => el.classList.contains('product__media-slide'));
      this.thumbs = $$('[data-thumb]', this);
      this.thumbs.forEach((thumb) => {
        on(thumb, 'click', () => this.select(thumb.dataset.thumb));
      });
    }

    select(mediaId) {
      this.slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === String(mediaId)));
      this.thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === String(mediaId)));
    }

    selectByElement(el) {
      this.select(el.dataset.mediaId);
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------------------------------------------------------- quantity */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      $$('button', this).forEach((btn) => {
        on(btn, 'click', (e) => {
          e.preventDefault();
          const step = btn.dataset.qtyChange === 'up' ? 1 : -1;
          const min = parseInt(this.input.min || '1', 10);
          const next = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------- bundle tiers */

  class BundleTiers extends HTMLElement {
    connectedCallback() {
      this.tiers = $$('.bundle-tier', this);
      this.qtyInput = document.getElementById(this.dataset.quantityTarget);
      this.variantInput = document.getElementById(this.dataset.variantTarget);

      this.tiers.forEach((tier) => {
        on(tier, 'click', () => this.select(tier));
        on(tier, 'keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.select(tier); }
        });
      });

      const preselected = this.querySelector('.bundle-tier.is-selected') || this.tiers[0];
      if (preselected) this.select(preselected);
    }

    select(tier) {
      this.tiers.forEach((t) => {
        const isMatch = t === tier;
        t.classList.toggle('is-selected', isMatch);
        const radio = t.querySelector('input[type="radio"]');
        if (radio) radio.checked = isMatch;
      });

      if (this.qtyInput && tier.dataset.quantity) {
        this.qtyInput.value = tier.dataset.quantity;
      }
      if (this.variantInput && tier.dataset.variantId) {
        this.variantInput.value = tier.dataset.variantId;
      }
    }
  }
  customElements.define('bundle-tiers', BundleTiers);

  /* ---------------------------------------------------------- sticky ATC */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.watchTarget = document.getElementById(this.dataset.watch);
      if (!this.watchTarget || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const passed = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
            this.classList.toggle('is-visible', passed);
          });
        },
        { rootMargin: '0px 0px 0px 0px', threshold: 0 }
      );
      this.observer.observe(this.watchTarget);

      const btn = this.querySelector('[data-sticky-submit]');
      if (btn) {
        on(btn, 'click', () => {
          const form = document.getElementById(this.dataset.form);
          if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
        });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------------------------------------------------- announcement bar */

  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      if (this.items.length < 2) return;
      this.index = 0;
      const speed = parseInt(this.dataset.speed || '5', 10) * 1000;
      this.timer = setInterval(() => this.next(), speed);
      on(this, 'mouseenter', () => clearInterval(this.timer));
    }
    next() {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* --------------------------------------------------------- sticky header */

  const headerWrapper = $('[data-header-sticky]');
  if (headerWrapper) {
    const header = $('.header', headerWrapper) || headerWrapper;
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-stuck', window.scrollY > 8);
      ticking = false;
    };
    on(window, 'scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* -------------------------------------------------------- collection sort */

  $$('[data-sort-select]').forEach((select) => {
    on(select, 'change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  });

  /* --------------------------------------------------------- cart page qty */

  $$('[data-cart-item-qty]').forEach((input) => {
    on(input, 'change', () => {
      const form = input.closest('form');
      if (!form) return;
      // Shopify's /cart POST needs the `update` param, so click the real
      // button rather than calling form.submit() (which drops button names).
      const updateBtn = form.querySelector('button[name="update"]');
      if (updateBtn) updateBtn.click();
    });
  });

  /* ------------------------------------------------------ scroll animation */

  function initScrollAnimations() {
    const targets = $$('[data-animate]');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const delay = parseInt(entry.target.dataset.animateDelay || '0', 10);
          setTimeout(() => entry.target.classList.add('is-in'), delay);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach((el) => io.observe(el));
  }
  initScrollAnimations();

  /* ------------------------------------------------- theme editor support */

  document.addEventListener('shopify:section:load', () => {
    initScrollAnimations();
  });
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = e.target.querySelector('cart-drawer');
    if (drawer) drawer.open();
  });
})();
